import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube } from "react-icons/fa";
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import SEO from '../SEO/SEO';
import { getYouTubeVideoId, resolveVideoUrl, resolveVideoThumbnail } from './BranchPageTemplate';

const faqData = [
  {
    question: "Am I eligible for a home loan?",
    answer: "Eligibility depends on your income size, and repayment capacity. Aham considers salaried, self-employed, and informal income applicants in Madurai, so speak with our branch team for a quick assessment even if you don't have conventional income proof."
  },
  {
    question: "What documents are required?",
    answer: "Typically ID proof, address proof, income or business proof, bank statements, and property documents. Our Madurai branch team collects these at your doorstep and guides you if any document is missing."
  },
  {
    question: "Can self-employed customers apply?",
    answer: "Yes, Aham specializes in serving self-employed individuals and small business owners in Madurai, using alternate income assessment methods where formal income proof like ITR isn't available."
  },
  {
    question: "How much loan can I get?",
    answer: "Loan amount depends on your income, property value, and repayment capacity. Our Madurai team calculates an indicative amount during your eligibility assessment, before you commit to anything."
  },
  {
    question: "How is EMI calculated?",
    answer: "EMI is based on the loan amount, interest rate, and tenure you choose. Our team shares a clear EMI breakdown upfront so there are no surprises during repayment."
  },
  {
    question: "How long does approval take?",
    answer: "Once documentation is complete, most applications at our Madurai branch are assessed within a few working days, depending on verification and property checks."
  },
  {
    question: "Can I apply for a construction loan?",
    answer: "Yes. If you own a plot in or around Madurai, our home construction loan is disbursed in stages that match your construction progress."
  },
  {
    question: "Do you offer loans against property?",
    answer: "Yes. You can unlock funds against a residential or commercial property you own in Madurai for personal or business needs."
  },
  {
    question: "Can I transfer my existing home loan?",
    answer: "Yes. Our Madurai branch can manage the full balance transfer process if you want to move your existing home loan to Aham for better terms."
  },
  {
    question: "How can I contact the Madurai branch?",
    answer: "Call us at +91 78239 11555, WhatsApp our team, or fill in the quick enquiry form on this page and we'll get back to you."
  }
];

const MaduraiBranch = () => {
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    city: 'Madurai',
    loanRequirement: 'Home Construction Loan'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const getApiUrl = (endpoint) => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const baseUrl = isLocal ? 'http://localhost:8001' : '';
    return `${baseUrl}${endpoint}`;
  };

  const handlePhoneChange = (e) => {
    const newPhone = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData(prev => ({ ...prev, mobile: newPhone }));
    if (otpSent || otpVerified) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
    }
  };

  const handleSendOtp = async () => {
    const cleanedMobile = formData.mobile.replace(/\D/g, '');
    if (!cleanedMobile || cleanedMobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsSendingOtp(true);
    const loadingToast = toast.loading('Sending OTP...');

    try {
      const response = await fetch(getApiUrl('/api/otp/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: cleanedMobile }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true);
        toast.success('OTP sent to your mobile number!', { id: loadingToast });
        if (data.dev_otp) {
          console.log('Dev OTP:', data.dev_otp);
        }
      } else {
        toast.error(data.message || 'Failed to send OTP. Please try again.', { id: loadingToast });
      }
    } catch (error) {
      console.error('OTP Send error:', error);
      toast.error('An error occurred while sending OTP. Please try again later.', { id: loadingToast });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    const verifyToast = toast.loading('Verifying OTP...');

    try {
      const response = await fetch(getApiUrl('/api/otp/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: formData.mobile, otp }),
      });
      const data = await response.json();
      if (data.success) {
        setOtpVerified(true);
        toast.success('OTP verified successfully!', { id: verifyToast });
      } else {
        toast.error(data.message || 'Invalid or expired OTP', { id: verifyToast });
      }
    } catch (error) {
      console.error('OTP Verify error:', error);
      toast.error('An error occurred during verification.', { id: verifyToast });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    const cleanedMobile = formData.mobile.replace(/\D/g, '');
    if (cleanedMobile.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    if (!otpVerified) {
      toast.error('Please verify your mobile number with OTP first');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Submitting your enquiry...');

    try {
      const payload = {
        fullName: formData.fullName.trim(),
        mobile: cleanedMobile,
        city: formData.city.trim() || 'Madurai',
        loanRequirement: formData.loanRequirement,
        branchName: 'Madurai Branch',
        slug: 'madurai',
        pageUrl: '/affordable-home-loan-in-madurai'
      };

      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = isLocal ? 'http://localhost:8001/api/gmb-branches/submit-enquiry' : '/api/gmb-branches/submit-enquiry';

      const response = await axios.post(apiUrl, payload);

      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Enquiry submitted successfully!', { id: loadingToast });
        navigate('/thank-you');
      } else {
        toast.error(response.data?.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
      }
    } catch (err) {
      console.error('Enquiry submission error:', err);
      toast.error(err.response?.data?.message || 'Failed to submit enquiry. Please try again.', { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const branchSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/affordable-home-loan-in-madurai#financialservice",
        "name": "Aham Housing Finance - Madurai Branch",
        "url": "https://ahamhfc.com/affordable-home-loan-in-madurai",
        "telephone": "+91 78239 11555",
        "description": "Affordable home loan in Madurai: purchase, composite (plot + construction), LAP & balance transfer. Visit our Madurai branch or call +91 78239 11555.",
        "areaServed": {
          "@type": "City",
          "name": "Madurai"
        }
      }
    ]
  };

  return (
    <div className="font-sans">
      <SEO
        title="Affordable Home Loan in Madurai | Aham Housing Finance"
        description="Affordable home loan in Madurai: purchase, composite (plot + construction), LAP & balance transfer. Visit our Madurai branch or call +91 78239 11555."
        keywords="home loan in Madurai, affordable home loan Madurai, housing finance Madurai, Aham Housing Finance Madurai, home construction loan Madurai, loan against property Madurai"
        canonical="https://ahamhfc.com/affordable-home-loan-in-madurai"
        ogTitle="Affordable Home Loan in Madurai | Aham Housing Finance"
        ogDescription="Affordable home loan in Madurai: purchase, composite (plot + construction), LAP & balance transfer. Visit our Madurai branch or call +91 78239 11555."
        schema={branchSchema}
      />
      {/* Hero Section */}
      <div className="bg-[#283079]/80 pt-24 pb-16">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">

            {/* Left Content Section */}
            <div className="text-white lg:w-[45%] space-y-6">

              {/* Breadcrumbs */}
              <div className="text-sm text-gray-300 flex space-x-2">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                {/* <span>›</span>
                <Link to="/branch-locator-aham-housing-finance-home-loan" className="hover:text-white transition-colors">Home Loan Branches</Link> */}
                <span>›</span>
                <span className="text-gray-400">Affordable Home Loan Madurai</span>
              </div>

              {/* Subheading */}
              <div className="flex items-center space-x-3 mt-4">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">MADURAI BRANCH</span>
              </div>

              {/* Main Heading */}
              <h1 className="text-4xl md:text-5xl lg:text-4xl xl:text-5xl font-bold leading-tight">
                Affordable Home <br />
                Loan <span className="text-yellow-500">In Madurai</span>
              </h1>

              {/* Description */}
              <p className="text-gray-300 text-base md:text-sm xl:text-base leading-relaxed max-w-xl">
                Looking for an affordable home loan in Madurai? Aham Housing Finance helps families, salaried employees, self-employed individuals, and first-time home buyers explore home loan options with personalised guidance.
              </p>
              <p className="text-gray-300 text-base md:text-sm xl:text-base leading-relaxed max-w-xl">
                Home loan without income proof. ₹10L to ₹50L, up to 20 years, with Competitive Interest Rates
              </p>

              {/* Action Buttons & Testimonials Group */}
              <div className="flex flex-col sm:inline-flex w-full sm:w-max">
                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <button
                    type="button"
                    style={{ backgroundColor: '#ffffff', color: '#283079' }}
                    className="hover:!bg-gray-100 font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    Check Your Eligibility
                  </button>
                  <button
                    type="button"
                    style={{ backgroundColor: '#ffffff', color: '#283079' }}
                    className="font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    Talk To A Loan Expert
                  </button>
                </div>

                {/* Testimonials & Reviews */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-start gap-4 sm:gap-6 pt-6 pb-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('youtube-videos-section')?.scrollIntoView({ behavior: 'smooth' })}
                    style={{ backgroundColor: '#ffffff', color: '#283079' }}
                    className="font-bold py-3 px-6 rounded-xl transition-all duration-300 shadow-2xl whitespace-nowrap border-0 cursor-pointer"
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#ffffff'}
                  >
                    Customers Voice
                  </button>

                  <div className="flex items-center gap-3 h-full pt-1">
                    <div className="flex flex-col">
                      <span className="text-white text-sm font-bold tracking-wide mb-1">Review & Rating</span>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center text-yellow-400">
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                          <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                        </div>
                        <span className="text-white text-xs font-medium">(4.5/5) (5 Reviews)</span>
                      </div>
                      <span className="text-white text-xs mt-1 font-medium tracking-wide">Madurai Branch</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features Checkmarks */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-y-6 pt-4 text-sm font-medium">
                <div className="flex items-center whitespace-nowrap">
                  <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  RBI Regulated HFC
                </div>
                <div className="flex items-center whitespace-nowrap">
                  <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Doorstep Documentation
                </div>
                <div className="flex items-center whitespace-nowrap">
                  <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  No ITR Required
                </div>
                <div className="flex items-center whitespace-nowrap">
                  <svg className="w-5 h-5 text-white mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Quick Disbursal
                </div>
              </div>

            </div>

            {/* Middle Form Section */}
            <div className="w-full lg:w-[30%] mt-10 lg:mt-0">
              <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-xl">
                <p className="text-xl lg:text-2xl font-bold text-[#283079] mb-2 font-serif">Quick Enquiry</p>
                <p className="text-gray-500 text-xs lg:text-sm mb-6">Get a callback from our Madurai branch within 24 hours.</p>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Full Name</label>
                    <input
                      type="text"
                      placeholder="Enter your name"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    />
                  </div>

                  {/* Mobile Number with Send OTP / Verified Button */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">
                      Mobile Number*
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="tel"
                        placeholder="10-digit mobile number"
                        maxLength={10}
                        value={formData.mobile}
                        onChange={handlePhoneChange}
                        disabled={otpVerified || isSubmitting}
                        required
                        className={`flex-1 bg-gray-50 border text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors ${
                          otpVerified ? 'border-green-500 bg-green-50 text-green-900 font-medium' : 'border-gray-200'
                        }`}
                      />
                      {!otpVerified ? (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={isSendingOtp || formData.mobile.length !== 10}
                          className={`px-3 sm:px-4 py-2 rounded-lg font-semibold text-xs transition-all whitespace-nowrap cursor-pointer border-0 ${
                            isSendingOtp || formData.mobile.length !== 10
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-[#283079] text-white hover:bg-[#1e245c]'
                          }`}
                        >
                          {isSendingOtp ? 'Sending...' : otpSent ? 'Resend OTP' : 'Send OTP'}
                        </button>
                      ) : (
                        <div className="flex items-center px-3 py-2 bg-green-100 text-green-700 rounded-lg text-xs font-bold whitespace-nowrap">
                          <CheckCircleIcon className="h-4 w-4 mr-1 text-green-600" />
                          Verified
                        </div>
                      )}
                    </div>
                  </div>

                  {/* OTP Input Card - Visible when OTP has been sent and not yet verified */}
                  {otpSent && !otpVerified && (
                    <div className="bg-blue-50/80 p-3.5 rounded-xl border border-blue-200 animate-fadeIn space-y-2">
                      <label className="block text-xs font-bold text-blue-900">
                        Enter 6-Digit OTP Sent To Your Mobile
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="6-digit OTP"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          className="flex-1 bg-white border border-blue-300 text-gray-800 text-center text-base tracking-widest font-mono font-bold rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block p-2.5 transition-colors"
                        />
                        <button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={isVerifyingOtp || otp.length !== 6}
                          className={`px-4 py-2 rounded-lg font-bold text-xs transition-all whitespace-nowrap border-0 cursor-pointer ${
                            isVerifyingOtp || otp.length !== 6
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                          }`}
                        >
                          {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                        </button>
                      </div>
                      <p className="text-[11px] text-blue-700 leading-tight">
                        Please enter the 6-digit code received via SMS.
                      </p>
                    </div>
                  )}

                  {/* City / Area */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">City / Area</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    />
                  </div>

                  {/* Loan Requirement */}
                  <div>
                    <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-1">Loan Requirement</label>
                    <select
                      value={formData.loanRequirement}
                      onChange={(e) => setFormData({ ...formData, loanRequirement: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-3 transition-colors"
                    >
                      <option value="Home Construction Loan">Home Construction Loan</option>
                      <option value="Home Renovation Loan">Home Renovation Loan</option>
                      <option value="Home Purchase Loan">Home Purchase Loan</option>
                      <option value="Composite Loan">Composite Loan</option>
                      <option value="Home Extension Loan">Home Extension Loan</option>
                      <option value="Balance Transfer Loan">Balance Transfer Loan</option>
                      <option value="NRI Home Loan">NRI Home Loan</option>
                      <option value="Top-up Loan">Top-up Loan</option>
                      <option value="Mortgage Loan">Mortgage Loan</option>
                    </select>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isSubmitting || !otpVerified}
                      style={{
                        backgroundColor: !otpVerified ? '#9CA3AF' : '#DC2626',
                        color: '#eff1f6ff'
                      }}
                      className="w-full font-bold py-3 px-4 rounded-xl transition-colors duration-300 shadow-sm border-0 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <span className="inline-flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : !otpVerified ? (
                        'Verify Mobile with OTP to Submit'
                      ) : (
                        'Submit Enquiry'
                      )}
                    </button>
                  </div>

                  {/* Disclaimer */}
                  <p className="text-[10px] text-gray-400 text-center mt-4 leading-tight">
                    By submitting, you agree to be contacted by Aham Housing Finance regarding your enquiry.
                  </p>
                </form>
              </div>

              {/* Social Media Links */}
              <div className="flex justify-center space-x-4 mt-6">
                <a href="https://www.facebook.com/ahamhfc" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm">
                  <FaFacebookF />
                </a>
                <a href="https://www.instagram.com/aham_housingfinance/" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm">
                  <FaInstagram />
                </a>
                <a href="https://linkedin.com/company/ahamhfc?originalSubdomain=in" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm">
                  <FaLinkedinIn />
                </a>
                <a href="https://www.youtube.com/@ahamhfc" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-yellow-500 hover:border-yellow-500 hover:text-[#283079] border border-white/20 text-white w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center text-lg shadow-sm">
                  <FaYoutube />
                </a>
              </div>
            </div>

            {/* Right Images Section */}
            <div className="w-full lg:w-[30%] mt-10 lg:mt-0">
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {/* Image 1 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Home Loan" className="w-full h-full object-cover" />
                </div>
                {/* Image 2 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Happy Family" className="w-full h-full object-cover" />
                </div>
                {/* Image 3 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="New Home" className="w-full h-full object-cover" />
                </div>
                {/* Image 4 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Construction" className="w-full h-full object-cover" />
                </div>
                {/* Image 5 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Interior" className="w-full h-full object-cover" />
                </div>
                {/* Image 6 */}
                <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Keys" className="w-full h-full object-cover" />
                </div>
                {/* Image 7 */}
                {/* <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Keys" className="w-full h-full object-cover" />
                </div> */}
                {/* Image 8 */}
                {/* <div className="aspect-square bg-white/10 rounded-xl overflow-hidden border border-white/20 hover:border-yellow-500 transition-colors shadow-lg">
                  <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80" alt="Construction" className="w-full h-full object-cover" />
                </div> */}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Features Strip */}
      <div className="bg-[#FFF8D6] py-5 border-b border-yellow-200">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center md:text-left text-[#283079] font-bold text-sm">
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
              RBI Regulated HFC
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
              Local Branch Support
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
              Personalised Guidance
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-2">
              <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path></svg>
              Home Loan Experts
            </div>
          </div>
        </div>
      </div>

      {/* YouTube Videos Section */}
      <div id="youtube-videos-section" className="bg-white py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
          <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-red-600"></div>
              <span className="text-red-600 font-semibold tracking-wider text-sm uppercase">LATEST VIDEOS</span>
              <div className="w-6 h-[2px] bg-red-600"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              Watch & Learn About Aham
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Discover our loan processes, customer stories, and branch highlights on our official YouTube channel.
            </p>
          </div>

          {/* Grid Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80", title: "Aham Madurai Branch Walkthrough", video_url: "https://www.youtube.com/shorts/A3zpiMO9BGw" },
              { img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80", title: "Customer Success Stories", video_url: "https://www.youtube.com/shorts/4CX5ZuWJ42U" },
              { img: "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80", title: "How to Apply for a Loan", video_url: "https://www.youtube.com/watch?v=Z-4ufIM8YTY" },
              { img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80", title: "Construction Loans Explained", video_url: "https://www.youtube.com/@ahamhfc" }
            ].map((video, idx) => {
              const videoHref = resolveVideoUrl(video, "https://www.youtube.com/@ahamhfc");
              const thumbSrc = resolveVideoThumbnail(video);

              return (
                <a
                  key={idx}
                  href={videoHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 block aspect-[9/16] cursor-pointer bg-gray-900"
                >
                  {/* Thumbnail */}
                  <img
                    src={thumbSrc}
                    alt={video.title}
                    onError={(e) => {
                      const fallbackId = getYouTubeVideoId(video.video_url || video.img);
                      if (fallbackId && e.target.src !== `https://img.youtube.com/vi/${fallbackId}/hqdefault.jpg`) {
                        e.target.src = `https://img.youtube.com/vi/${fallbackId}/hqdefault.jpg`;
                      }
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-70 group-hover:opacity-50"
                  />

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 bg-red-600/90 rounded-full flex items-center justify-center text-white shadow-lg group-hover:bg-red-600 group-hover:scale-110 transition-all duration-300">
                      <FaYoutube className="w-6 h-6" />
                    </div>
                  </div>

                  {/* Video Title */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
                    <h3 className="text-white font-bold text-sm line-clamp-2">{video.title}</h3>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://www.youtube.com/@ahamhfc"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-red-600 font-bold hover:text-red-700 transition-colors bg-red-50 hover:bg-red-100 py-3 px-6 rounded-full"
            >
              <span>Visit Our YouTube Channel</span>
              <FaYoutube className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Why Choose Section */}
      <div className="bg-[#F4F6F9] py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl">

          <div className="mb-12 max-w-3xl">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">WHY AHAM</span>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-6 font-serif">
              Why Choose Aham for a Home Loan in Madurai
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Families across Madurai choose Aham Housing Finance because we go beyond paperwork — our local branch team understands income patterns unique to Madurai's traders, weavers, small business owners, and salaried residents, and works with you to find a loan that actually fits.
            </p>
          </div>

          {/* Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Card 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <span className="text-yellow-500 font-bold text-lg block mb-4">01</span>
              <p className="text-xl font-bold text-[#283079] mb-3 font-serif">Local Branch Support</p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Our Madurai team meets you where you are — at the branch or at your doorstep — to guide you through eligibility and documentation in Tamil or English.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <span className="text-yellow-500 font-bold text-lg block mb-4">02</span>
              <p className="text-xl font-bold text-[#283079] mb-3 font-serif">Affordable Home Loan Solutions</p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Loan products designed for real incomes, including flexible options for self-employed and informal-income borrowers across Madurai.
              </p>


            </div>

            {/* Card 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <span className="text-yellow-500 font-bold text-lg block mb-4">03</span>
              <p className="text-xl font-bold text-[#283079] mb-3 font-serif">Guidance Throughout The Process</p>
              <p className="text-gray-500 text-sm leading-relaxed">
                From your first enquiry to disbursement, a dedicated relationship manager keeps you informed at every step — no guesswork, no runaround.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <span className="text-yellow-500 font-bold text-lg block mb-4">04</span>
              <p className="text-xl font-bold text-[#283079] mb-3 font-serif">Dedicated Customer Assistance</p>
              <p className="text-gray-500 text-sm leading-relaxed">
                Post-disbursement support for EMI queries, statements, and top-up or balance transfer needs, handled locally by our Madurai branch.
              </p>
            </div>

          </div>

        </div>
      </div>

      {/* Buying a Home in Madurai Section */}
      <div className="bg-white py-20 border-t border-gray-100">
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">LOCAL INSIGHTS</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              Buying a Home in Madurai: What to Check Before Your Home Loan
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Based on the borrowers we serve at our Madurai branch, here are the key trends and facts about the local real estate market.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Locality */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#283079]/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#283079]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.243-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              </div>
              <h3 className="font-bold text-[#283079] mb-2">Popular Localities</h3>
              <p className="text-gray-600 text-sm leading-relaxed">People are frequently buying in emerging and established areas like Othakadai, Thirumangalam, Iyer Bungalow, and Villapuram.</p>
            </div>

            {/* Property Types */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
              </div>
              <h3 className="font-bold text-[#283079] mb-2">Property Types</h3>
              <p className="text-gray-600 text-sm leading-relaxed">We see high demand for independent houses, residential plots for custom construction, and affordable apartments.</p>
            </div>

            {/* Approvals */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#283079]/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#283079]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              <h3 className="font-bold text-[#283079] mb-2">Authority Approvals</h3>
              <p className="text-gray-600 text-sm leading-relaxed">We process loans for properties with clear titles approved by DTCP and the Madurai Local Planning Authority (LPA).</p>
            </div>

            {/* Borrowers */}
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path></svg>
              </div>
              <h3 className="font-bold text-[#283079] mb-2">Our Borrowers</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Our branch predominantly supports small business owners, local traders, weavers, self-employed individuals, and salaried professionals.</p>
            </div>
          </div>
        </div>
      </div>
      {/* Loan Products Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl">

          <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">LOAN PRODUCTS</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              Home Loan Types in Madurai
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              Whether you're buying, building, renovating, or extending, our Madurai branch offers a full range of housing finance products.
            </p>
          </div>

          {/* Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">


            {/* Card 2 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Home Construction Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Build your dream home on your own plot in Madurai with staged disbursement matched to construction progress.
              </p>
              <Link to="/home-construction-loan-india" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 1 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Composite  Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Composite Home Loan in Madurai – buy a plot and build your house in one loan
              </p>
              <Link to="/composite-home-loan-plot-construction" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>



            {/* Card 3 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Home Improvement Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Renovate, repaint, or upgrade your existing Madurai home with a loan sized to the work you need done.
              </p>
              <Link to="/home-renovation-loan" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Home Extension Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Add a floor or extra room as your family grows, financed against your existing Madurai property.
              </p>
              <Link to="/home-extension-loan" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Loan Against Property in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Unlock funds against a residential or commercial property you own in Madurai for any personal or business need.
              </p>
              <Link to="/loan-against-property-mortgage-loan" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 6 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Balance Transfer Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Move your existing home loan to Aham for better terms, with the process managed by our Madurai team.
              </p>
              <Link to="/home-loan-balance-transfer" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 7 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">Top-Up Loan in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Existing Aham customers in Madurai can access additional funds on top of their running home loan.
              </p>
              <Link to="/top-up-loan-for-home-loan" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

            {/* Card 8 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center text-[#283079] mb-4">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
              </div>
              <h3 className="text-lg font-bold text-[#283079] mb-3">PMAY Support in Madurai</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-grow">
                Check your eligibility for PMAY subsidy benefits with guided application support from our branch.
              </p>
              <Link to="/pmay-2-0-scheme-apply-online" className="text-[#283079] font-bold text-sm inline-flex items-center hover:text-yellow-600 transition-colors mt-auto">
                Learn More <span className="ml-1">→</span>
              </Link>
            </div>

          </div>

        </div>
      </div>
      {/* The Process Section */}
      <div className="bg-[#F4F6F9] py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl">

          <div className="text-center mb-16 max-w-3xl mx-auto flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">THE PROCESS</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              How Our Home Loan Process Works
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              A simple, transparent six-step journey from enquiry to disbursement — guided by our Madurai team throughout.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative max-w-6xl mx-auto">
            {/* Horizontal Dashed Line (Desktop only) */}
            <div className="hidden md:block absolute top-6 left-[8%] right-[8%] h-[2px] border-t-2 border-dashed border-gray-300 z-0"></div>

            <div className="grid grid-cols-1 md:grid-cols-6 gap-6 md:gap-4 text-center relative z-10">

              {/* Step 1 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">1</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Submit Enquiry</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Share your details via our quick form, call, or WhatsApp.
                </p>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">2</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Eligibility Assessment</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Our Madurai team reviews your income and loan requirement.
                </p>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">3</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Documentation</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Doorstep collection of KYC, income, and property documents.
                </p>
              </div>

              {/* Step 4 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">4</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Verification</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Property and document checks are completed by our team.
                </p>
              </div>

              {/* Step 5 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">5</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Approval</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Your loan is sanctioned with clear terms and EMI details.
                </p>
              </div>

              {/* Step 6 */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-[#283079] text-white rounded-full flex items-center justify-center font-bold text-lg mb-4 mx-auto border-4 border-[#F4F6F9] shadow-sm relative z-10">6</div>
                <p className="font-bold text-[#283079] text-sm mb-2">Loan Disbursement</p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[150px]">
                  Funds are disbursed directly, in line with your loan purpose.
                </p>
              </div>

            </div>
          </div>

        </div>
      </div>
      {/* Eligibility Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-5xl">

          <div className="text-center mb-12 max-w-2xl mx-auto flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">ELIGIBILITY</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              Home Loan Eligibility and Documents in Madurai
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              We check your real income, not just your papers. That is why many people in Madurai can get a home loan with Aham.
            </p>
          </div>

          {/* Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

            {/* Card 1 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">Cash Salaried Employees</h3>
                <p className="text-gray-500 text-xs">Salary certificate from the company.</p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">Self-Employed</h3>
                <p className="text-gray-500 text-xs">Bank statements, GST or business papers. 
                  {/* A home loan for self-employed people without ITR is possible. */}
                  </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">Small Business Owners</h3>
                <p className="text-gray-500 text-xs">Shop or business proof and bank statements.</p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">First-Time Home Buyers</h3>
                <p className="text-gray-500 text-xs">Step by step assistance at doorstep.</p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">Affordable Housing Customers</h3>
                <p className="text-gray-500 text-xs">A loan sized to your real income.</p>
              </div>
            </div>

            {/* Card 6 */}
            <div className="bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex items-start space-x-4">
              <div className="text-yellow-600 flex-shrink-0 mt-1">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 11l3 3L22 4"></path></svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 text-sm mb-1">Informal Income Customers</h3>
                <p className="text-gray-500 text-xs">No income proof? We check your income in other ways.</p>
              </div>
            </div>

          </div>
        </div>
      </div>
      {/* Customer Stories Section */}
      <div className="bg-[#F4F6F9] py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-6xl">

          <div className="text-center mb-12 max-w-3xl mx-auto flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">CUSTOMER STORIES</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              What Our Madurai Branch Customers Say
            </h2>

            {/* Paragraph */}
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Sample reviews shown for layout purposes — to be replaced with verified<br className="hidden md:block" /> Google Business Profile reviews from the Madurai branch before launch.
            </p>
          </div>

          {/* Grid Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

            {/* Card 1 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="flex text-yellow-500 mb-4 text-sm space-x-1">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
              </div>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow">
                "Applied for a home construction loan at the Madurai branch. The team explained every step clearly and the doorstep documentation saved me multiple trips."
              </p>
              <div className="flex items-center space-x-3 mt-auto">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#283079] font-bold text-xs flex items-center justify-center flex-shrink-0">MR</div>
                <div className="flex flex-col">
                  <p className="font-bold text-[#283079] text-xs">Muthu Raman</p>
                </div>
                <div className="ml-auto flex items-center text-gray-400 text-[10px]">
                  <span>- Sample review</span>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="flex text-yellow-500 mb-4 text-sm space-x-1">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
              </div>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow">
                "As a self-employed tailor, I was worried about eligibility. Aham looked at my actual income and approved my loan without asking for ITR."
              </p>
              <div className="flex items-center space-x-3 mt-auto">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#283079] font-bold text-xs flex items-center justify-center flex-shrink-0">KS</div>
                <div className="flex flex-col">
                  <p className="font-bold text-[#283079] text-xs">Kavitha S.</p>
                </div>
                <div className="ml-auto flex items-center text-gray-400 text-[10px]">
                  <span>- Sample review</span>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="flex text-yellow-500 mb-4 text-sm space-x-1">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
              </div>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow">
                "Transferred my existing home loan to Aham for a better rate. The Madurai branch handled the entire balance transfer process for me."
              </p>
              <div className="flex items-center space-x-3 mt-auto">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#283079] font-bold text-xs flex items-center justify-center flex-shrink-0">AP</div>
                <div className="flex flex-col">
                  <p className="font-bold text-[#283079] text-xs">Arun Prakash</p>
                </div>
                <div className="ml-auto flex items-center text-gray-400 text-[10px]">
                  <span>- Sample review</span>
                </div>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col h-full">
              <div className="flex text-yellow-500 mb-4 text-sm space-x-1">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path></svg>
              </div>
              <p className="text-gray-500 text-xs md:text-sm leading-relaxed mb-6 flex-grow">
                "Got a top-up loan on my existing home loan within days. Friendly staff, transparent EMI breakdown, no hidden charges."
              </p>
              <div className="flex items-center space-x-3 mt-auto">
                <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#283079] font-bold text-xs flex items-center justify-center flex-shrink-0">LD</div>
                <div className="flex flex-col">
                  <p className="font-bold text-[#283079] text-xs">Lakshmi Devi</p>
                </div>
                <div className="ml-auto flex items-center text-gray-400 text-[10px]">
                  <span>- Sample review</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>


      {/* Visit Us Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Left Content */}
            <div className="lg:w-1/2">
              <div className="mb-8">
                {/* Subheading */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-6 h-[2px] bg-yellow-500"></div>
                  <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">VISIT US</span>
                </div>
                {/* Heading */}
                <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
                  Aham Housing Finance Madurai Branch
                </h2>
                {/* Paragraph */}
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                  Our Madurai branch serves families, first-time buyers, and self-employed borrowers across the city — helping you move closer to home ownership with local, on-ground support.
                </p>
              </div>

              {/* Branch Information Card */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 shadow-sm">
                <h3 className="text-xl font-bold text-[#283079] mb-6 font-serif">Madurai Branch Information</h3>

                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start pb-4 border-b border-gray-100">
                    <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">ADDRESS</p>
                      <p className="text-sm font-bold text-gray-800">1st Floor, 125-S, PP Nagar Main Road, 1, Theni Rd, Kalavasal</p>
                      <p className="text-xs text-gray-500 mt-0.5">Madurai, Tamil Nadu 625016</p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start pb-4 border-b border-gray-100">
                    <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path></svg>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">PHONE</p>
                      <p className="text-sm font-bold text-gray-800">+91 78239 11555</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start pb-4 border-b border-gray-100">
                    <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">EMAIL</p>
                      <p className="text-sm font-bold text-gray-800">info@ahamhfc.com</p>
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div className="flex items-start pb-4 border-b border-gray-100">
                    <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">WORKING HOURS</p>
                      <p className="text-sm font-bold text-gray-800">Mon - Sat, 9:30 AM — 6:30 PM</p>
                    </div>
                  </div>

                  {/* Landmark */}
                  <div className="flex items-start pb-6">
                    <svg className="w-5 h-5 text-gray-500 mr-4 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path></svg>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">LANDMARK</p>
                      <p className="text-sm font-bold text-gray-800">Kalavasal</p>
                    </div>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex flex-wrap gap-3">
                  <a
                    href="tel:+917823911555"
                    className="border border-[#283079] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-50 transition-colors duration-300 inline-flex items-center"
                  >
                    Call Branch
                  </a>
                  <a
                    href="https://maps.app.goo.gl/FAQR1jtgfowF8Phf8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#EEF2FF] text-[#283079] font-bold text-xs py-2.5 px-5 rounded-full hover:bg-indigo-100 transition-colors duration-300 inline-flex items-center"
                  >
                    Get Directions
                  </a>
                </div>
              </div>
            </div>

            {/* Right Map */}
            <div className="lg:w-1/2">
              <div className="rounded-2xl overflow-hidden w-full h-[300px] lg:h-full min-h-[400px] shadow-sm border border-gray-100">
                <iframe
                  title="Madurai Branch Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  allowFullScreen
                  referrerPolicy="no-referrer-when-downgrade"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3930.0308362317614!2d78.09162537450779!3d9.931389874212615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3b00cfc2b3df5ebb%3A0x91d23cfffd84be13!2sAham%20Housing%20Finance%20Limited%20-%20Madurai!5e0!3m2!1sen!2sin!4v1792999803320!5m2!1sen!2sin"
                ></iframe>
              </div>
            </div>

          </div>
        </div>
      </div>
      {/* Gallery Section */}
      <div className="bg-white py-16 overflow-hidden border-t border-gray-100">
        <style>
          {`
            @keyframes infinite-scroll {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .animate-infinite-scroll {
              display: flex;
              width: max-content;
              animation: infinite-scroll 30s linear infinite;
            }
            .animate-infinite-scroll:hover {
              animation-play-state: paused;
            }
          `}
        </style>
        <div className="container mx-auto px-4 lg:px-12 max-w-7xl mb-10">
          <div className="flex items-center space-x-3 mb-4 justify-center">
            <div className="w-6 h-[2px] bg-yellow-500"></div>
            <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">GALLERY</span>
            <div className="w-6 h-[2px] bg-yellow-500"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#283079] text-center font-serif">
            Life At Our Branch
          </h2>
        </div>

        <div className="relative w-full">
          <div className="animate-infinite-scroll space-x-4 md:space-x-6 px-4">
            {[
              "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
              "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
            ].map((img, idx) => (
              <div key={idx} className="w-[260px] md:w-[320px] h-[200px] md:h-[240px] flex-shrink-0 rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-transform hover:-translate-y-1">
                <img src={img} alt={`Gallery item ${idx}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Coverage Section */}
      <div className="bg-[#F4F6F9] py-16">
        <div className="container mx-auto px-4 lg:px-12 max-w-5xl text-center">

          {/* Subheading */}
          <div className="flex items-center space-x-3 mb-4 justify-center">
            <div className="w-6 h-[2px] bg-yellow-500"></div>
            <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">COVERAGE</span>
            <div className="w-6 h-[2px] bg-yellow-500"></div>
          </div>

          {/* Heading */}
          <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-8 font-serif">
            Areas We Serve Near Madurai Branch
          </h2>

          {/* Pill Container */}
          <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Madurai</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Melur</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Thirumangalam</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Usilampatti</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Tirupparankundram</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Koodal Nagar</span>
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">Anuppanadi</span>
          </div>

          {/* Bottom Pill */}
          <div className="mt-5 flex justify-center">
            <span className="bg-white text-[#283079] font-medium text-sm md:text-base py-2.5 px-6 md:py-3 md:px-8 rounded-full shadow-sm border border-gray-100">+ Nearby Villages & Towns</span>
          </div>

          {/* Nearby Branches */}
          <div className="mt-16 border-t border-gray-200 pt-10">
            <h3 className="text-2xl md:text-3xl font-bold text-[#283079] mb-8 font-serif">
              Aham Branches Near Madurai
            </h3>
            <div className="flex flex-wrap justify-center gap-5 max-w-3xl mx-auto">
              <Link to="/branches/theni" className="bg-white border-2 border-[#283079] text-[#283079] hover:bg-[#283079] hover:text-white font-bold text-base md:text-lg py-3 px-10 rounded-full transition-colors duration-300">
                Home Loan in Theni
              </Link>
              <Link to="/branches/dindigul" className="bg-white border-2 border-[#283079] text-[#283079] hover:bg-[#283079] hover:text-white font-bold text-base md:text-lg py-3 px-10 rounded-full transition-colors duration-300">
                Home Loan in Dindigul
              </Link>
              <Link to="/branches/virudhunagar" className="bg-white border-2 border-[#283079] text-[#283079] hover:bg-[#283079] hover:text-white font-bold text-base md:text-lg py-3 px-10 rounded-full transition-colors duration-300">
                Home Loan in Virudhunagar
              </Link>
              <Link to="/branches/sivakasi" className="bg-white border-2 border-[#283079] text-[#283079] hover:bg-[#283079] hover:text-white font-bold text-base md:text-lg py-3 px-10 rounded-full transition-colors duration-300">
                Home Loan in Sivakasi
              </Link>
            </div>
          </div>

        </div>
      </div>
      {/* FAQ Section */}
      <div className="bg-white py-20">
        <div className="container mx-auto px-4 lg:px-12 max-w-4xl">

          <div className="text-center mb-12 flex flex-col items-center">
            {/* Subheading */}
            <div className="flex items-center space-x-3 mb-4 justify-center">
              <div className="w-6 h-[2px] bg-yellow-500"></div>
              <span className="text-yellow-500 font-semibold tracking-wider text-sm uppercase">FAQ</span>
              <div className="w-6 h-[2px] bg-yellow-500"></div>
            </div>

            {/* Heading */}
            <h2 className="text-3xl md:text-4xl font-bold text-[#283079] mb-4 font-serif">
              Home Loan FAQs for Madurai
            </h2>
          </div>

          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm cursor-pointer transition-all duration-200 hover:border-gray-300"
                onClick={() => toggleFaq(index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-bold text-[#283079] text-sm md:text-base">{faq.question}</h3>
                  <svg
                    className={`w-4 h-4 text-yellow-500 flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaqIndex === index ? 'max-h-40 opacity-100 mt-3' : 'max-h-0 opacity-0'}`}
                >
                  <p className="text-gray-500 text-xs md:text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="bg-[#F4F6F9] py-16 pb-24">
        <div className="container mx-auto px-4 lg:px-12 max-w-6xl">
          <div className="bg-[#283079] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden shadow-lg">
            {/* Subtle left glow overlay */}
            <div className="absolute top-0 left-0 w-64 h-full bg-white opacity-[0.03] mix-blend-overlay blur-3xl transform -translate-x-1/2"></div>

            <div className="relative z-10 flex flex-col items-center">
              {/* Subheading */}
              <div className="flex items-center space-x-3 mb-6 justify-center">
                <div className="w-6 h-[2px] bg-yellow-500"></div>
                <span className="text-yellow-500 font-semibold tracking-wider text-xs md:text-sm uppercase">GET STARTED</span>
                <div className="w-6 h-[2px] bg-yellow-500"></div>
              </div>

              {/* Heading */}
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-6 font-serif">
                Ready To Explore Your Home Loan Options?
              </h2>

              {/* Paragraph */}
              <p className="text-gray-300 text-xs md:text-sm leading-relaxed max-w-2xl mx-auto mb-10">
                Speak with our Madurai team to understand eligibility, documents required, <br className="hidden md:block" /> and available loan options.
              </p>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4 w-full sm:w-auto">
                {/* <button className="bg-yellow-500 text-gray-900 font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-yellow-400 transition-colors shadow-sm whitespace-nowrap">
                  Check Eligibility Now
                </button> */}
                <button className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap">
                  Check Eligibility Now
                </button>
                <button className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap">
                  Request A Callback
                </button>
                <button className="bg-transparent border border-gray-400 text-white font-bold text-xs md:text-sm py-3 md:py-3.5 px-6 md:px-8 rounded-full hover:bg-white/10 transition-colors whitespace-nowrap">
                  WhatsApp Our Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaduraiBranch;
